import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  AFPTable,
  Pagination,
  EmptyState,
  Spinner,
  ErrorMessage,
} from '@gsa/afp-component-library';
import { CHANGE_TYPES } from './utils/notification-center-utils';
import useDidMountEffect from '../../../hooks/use-did-mount';
import BidChangeNotificationTableSubRow from './change-notification-table-sub-row';
import { emDashUnicode } from '../../../utilities/constants';

const BidCommunicationCenterTable = ({
  tableData,
  error,
  isDataLoading,
  paginationState,
  setPaginationState,
  refetch,
}) => {
  const tableRef = React.createRef();


  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Vehicle Type',
        accessor: 'vehicleType',
        sortable: false,
        Cell: ({ row }) =>
          `${row.original.standardItem.vehicleTypeCode.title}` || emDashUnicode,
      },
      {
        Header: 'Standard Item',
        accessor: 'standardItem.title',
        sortable: false,
        Cell: ({ row }) =>
          `${row.original.standardItem.standardItemNumber}-${row.original.standardItem.title}`,
      },
      {
        Header: 'Equipment Code',
        accessor: 'equipmentCode.title',
        sortable: false,
        Cell: ({ row }) =>
          (row?.original?.equipmentCode &&
            `${row?.original?.equipmentCode?.code} - ${row?.original?.equipmentCode?.title}`) ||
          emDashUnicode,
      },
      {
        Header: 'Change Type',
        accessor: 'changeType',
        sortable: false,
        Cell: ({ value }) => CHANGE_TYPES[value] || value || emDashUnicode,
      },
      {
        Header: 'Change Date',
        accessor: 'createdAt',
        Cell: ({ value }) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
      },
    ];
    return columnList;
  }, []);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    const offset = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  useDidMountEffect(() => {
    refetch({
      limit: paginationState.limit,
      offset: paginationState.offset,
    });
  }, [paginationState]);

  const renderRowSubComponent = useCallback(({ row }) => {
    // if row.original?.oldValue && row.original?.newValue is null/undefined, do not render subrow
    if (!row.original?.oldValue && !row.original?.newValue) {
      return null;
    }
    return <BidChangeNotificationTableSubRow row={row} />;
  }, []);

  if (error)
    return (
      <div className="padding-left-6 padding-bottom-6">
        <ErrorMessage>Something went wrong</ErrorMessage>
      </div>
    );
  if (isDataLoading) return <Spinner />;

  return (
    <>
      <AFPTable
        testId="bid-notification-table-test-id"
        fullWidth
        ref={tableRef}
        columns={columns}
        data={tableData?.rows || []}
        expandable
        defaultSort="createdAt desc"
        renderRowSubComponent={renderRowSubComponent}
      />
      {tableData?.rows?.length > 0 && (
        <Pagination
          itemsPerPageOptions={[100, 200, 300]}
          onPageChange={handlePaginationChange}
          variant="advanced"
          itemsPerPage={paginationState.limit}
          currentPage={paginationState.currentPage}
          itemsCount={tableData?.count}
        />
      )}
      {!tableData?.rows?.length && (
        <div className="text-center margin-top-neg-2 height-full">
          <EmptyState
            hasBackground
            containerStyles="padding-top-9 height-full"
            topText={
              <p
                aria-label="There are no changes."
                // eslint-disable-next-line
                tabIndex={0}
              >
                <strong>There are no changes.</strong>
              </p>
            }
          />
        </div>
      )}
    </>
  );
};

BidCommunicationCenterTable.propTypes = {
  tableData: PropTypes.objectOf(PropTypes.any).isRequired,
  error: PropTypes.objectOf(PropTypes.any).isRequired,
  isDataLoading: PropTypes.bool.isRequired,
};

export default BidCommunicationCenterTable;
